<template>
  <button
    :disabled="disabled"
    :tabindex="tabindex"
    :title="title"
    @click="clicked"
    :class="cssClasses">
    <icon
      v-if="hasIcon"
      :name="iconName"
      :primaryColor="iconColor"
      :strokeColor="iconStroke"
      :size="getIconSize" />
    <img
      v-if="hasLogo"
      class="button-logo"
      :src="require('./../../assets/img/logos/' + logoName)">
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: 'btn',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: Number,
      default: 0
    },
    url: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    target: {
      type: String,
      default: '_self'
    },
    iconName: {
      type: String,
      default: ''
    },
    iconColor: {
      type: String,
      default: ''
    },
    iconStroke: {
      type: String,
      default: ''
    },
    iconSize: {
      type: String,
      default: ''
    },
    logoName: {
      type: String,
      default: ''
    }
  },
  computed: {
    cssClasses () {
      let classes = this.type.split(' ');

      return {
        'button': true,
        'button-active': classes.indexOf('active') > -1,
        'button-small': classes.indexOf('small') > -1,
        'button-medium': classes.indexOf('medium') > -1,
        'button-loader': classes.indexOf('loader') > -1,
        'button-wide': classes.indexOf('wide') > -1,
        'button-full-width': classes.indexOf('full-width') > -1,
        'button-narrow': classes.indexOf('narrow') > -1,
        'button-success': classes.indexOf('success') > -1,
        'button-neutral': classes.indexOf('neutral') > -1,
        'button-danger': classes.indexOf('danger') > -1,
        'button-icon': classes.indexOf('icon') > -1,
        'button-icon-larger': classes.indexOf('icon-larger') > -1,
        'button-icon-smaller': classes.indexOf('icon-smaller') > -1,
        'button-with-icon': classes.indexOf('with-icon') > -1,
        'button-popup-left': classes.indexOf('popup-left') > -1,
        'button-popup-right': classes.indexOf('popup-right') > -1,
        'button-popup-full': classes.indexOf('popup-full') > -1,
        'button-blue': classes.indexOf('blue') > -1,
        'button-inline': classes.indexOf('inline') > -1,
        'button-desktop-only': classes.indexOf('desktop-only') > -1,
        'button-mobile-only': classes.indexOf('mobile-only') > -1,
        'button-centered': classes.indexOf('centered') > -1,
        'button-google': classes.indexOf('google') > -1,
        'button-with-logo': this.hasLogo
      };
    },
    hasLogo () {
      return !!this.logoName;
    },
    hasIcon () {
      let classes = this.type.split(' ');
      return classes.indexOf('icon') > -1 || classes.indexOf('with-icon') > -1;
    },
    getIconSize () {
      if (this.iconSize !== '') {
        return this.iconSize;
      }

      let classes = this.type.split(' ');

      if (classes.indexOf('icon-larger') > -1) {
        return 's';
      }

      if (classes.indexOf('icon') > -1) {
        return 'm';
      }

      if (classes.indexOf('with-icon') > -1) {
        return 'xxs';
      }
    }
  },
  methods: {
    clicked () {
      if (this.url) {
        if (this.urlIsInternal()) {
          this.$router.push(this.url);
        } else {
          if (this.urlIsEmailOrPhone()) {
            window.location.href = this.url;
          } else {
            if (this.target === '_self') {
              window.location.href = this.url;
            } else if (this.target === '_blank') {
              let win = window.open(this.url, '_blank');
              win.focus();
            }
          }
        }
      }
    },
    urlIsInternal () {
      if (this.url.indexOf('mailto:') === 0 || this.url.indexOf('https://') === 0 || this.url.indexOf('http://') === 0) {
        return false;
      }

      return true;
    },
    urlIsEmailOrPhone () {
      if (this.url.indexOf('mailto:') === 0 || this.url.indexOf('tel:') === 0) {
        return true;
      }

      return false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.button {
  align-items: center;
  background: linear-gradient($color-button-default-top, $color-button-default-bottom);
  border: 1px solid $color-button-default-border;
  border-radius: $border-radius-small;
  color: $color-text;
  cursor: pointer;
  display: flex;
  font-size: 1.3rem;
  font-weight: 600;
  height: 3.6rem;
  line-height: 3.4rem;
  margin: 0 .3rem;
  padding: 0 1rem;
  transition: all .25s ease-out;
  white-space: nowrap;

  &:active,
  &:focus,
  &:hover {
    background: linear-gradient($color-button-default-bottom, $color-button-default-top);
    color: $color-text-dark;
  }

  &-active {
    background: $color-light;
    border-radius: $border-radius-small $border-radius-small 0 0;
    border-bottom: 2px solid $color-notice;
  }

  &-blue {
    background: $color-notice;
    border-color: $color-notice;
    color: $color-light;

    &:active,
    &:focus,
    &:hover {
      background: $color-primary;
      border-color: $color-primary;
      color: $color-light;
    }
  }

  &-neutral {
    background: linear-gradient($color-notice, darken($color-notice, 5%));
    border-color: darken($color-notice, 5%);
    color: $color-light;

    &:active,
    &:focus,
    &:hover {
      background: linear-gradient(darken($color-notice, 5%), $color-notice);
      color: $color-light;
    }
  }

  &-icon {
    line-height: 3.6rem;
    margin: 0 auto;
    padding: 0;
    width: 3.6rem;

    svg {
      margin: .5rem auto;
      overflow: unset;
    }

    &-larger {
      padding: .25rem 1rem;
      width: 4.2rem;

      &.button-with-icon {
        width: auto;
      }
    }

    &-smaller {
      svg {
        padding: .25rem .35rem;
      }
    }
  }

  &.button-with-logo {
    position: relative;
  }

  &-logo {
    height: 16px;
    left: 10px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: auto;
  }

  &-with-icon {
    & > svg {
      margin-right: .5rem;
    }
  }

  &-loader {
    svg {
      animation-name: rotate;
      animation-duration: 1s;
      animation-iteration-count: infinite;
      animation-timing-function: linear;
    }
  }

  &[disabled] {
    opacity: .5;
    pointer-events: none;
  }

  &-success {
    background: linear-gradient($color-button-success-top, $color-button-success-bottom)!important;
    border: none;
    color: $color-light!important;
    text-shadow: 1px 0 1px rgba(0, 0, 0, .25)!important;

    &:active,
    &:focus,
    &:hover {
      background: linear-gradient($color-button-success-bottom, $color-button-success-top)!important;
      color: $color-light!important;
    }
  }

  &-danger {
    background: linear-gradient($color-button-danger-top, $color-button-danger-bottom)!important;
    border: none;
    color: $color-light!important;
    display: block;
    text-shadow: 1px 0 1px rgba(0, 0, 0, .25);

    &:active,
    &:focus,
    &:hover {
      background: linear-gradient($color-button-danger-bottom, $color-button-danger-top)!important;
      color: $color-light!important;
    }
  }

  &-narrow {
    width: 3.6rem;
  }

  &-wide {
    font-size: 1.4rem;
    font-weight: bold;
    justify-content: center;
    width: 16rem;
  }

  &-full-width {
    justify-content: center;
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }

  &-medium {
    font-size: 1.2rem;
    height: 3.2rem;
    line-height: 3.2rem;
  }

  &-small {
    font-size: 1.2rem;
    height: 2.6rem;
    line-height: 2.4rem;
    padding: 0 1rem;

    svg {
      margin-right: .5rem;
    }
  }

  &-inline {
    display: inline-block;
    height: 2.4rem;
    line-height: 2.2rem;
    padding: 0 .5rem;
  }

  &-popup-left,
  &-popup-right,
  &-popup-full {
    background: linear-gradient($color-button-success-top, $color-button-success-bottom);
    border: none;
    border-radius: 0;
    bottom: 0;
    color: $color-light;
    height: 4.8rem;
    justify-content: center;
    left: 0;
    line-height: 4.8rem;
    margin: 0;
    position: absolute;
    width: 50%;

    &:active,
    &:focus,
    &:hover {
      background: linear-gradient($color-button-success-bottom, $color-button-success-top);
      color: $color-light;
    }

    &.button-danger {
      background: linear-gradient($color-button-danger-top, $color-button-danger-bottom);

      &:active,
      &:focus,
      &:hover {
        background: linear-gradient($color-button-danger-bottom, $color-button-danger-top);
        color: $color-light;
      }
    }
  }

  &-popup-right,
  &-popup-full {
    background: linear-gradient($color-button-default-top, $color-button-default-bottom);
    border-top: 1px solid $color-border;
    color: $color-text;
    left: auto;
    right: 0;
    width: 50%;

    &:active,
    &:focus,
    &:hover {
      background: linear-gradient($color-button-default-bottom, $color-button-default-top);
      color: $color-text-dark;
    }
  }

  &-popup-full {
    border-radius: 0 0 $border-radius-small $border-radius-small;
    color: $color-light;
    width: 100%;
  }

  &-centered {
    justify-content: center;
    padding-top: 0;
    padding-bottom: 0;
  }

  &-google,
  &-google:active,
  &-google:focus,
  &-google:hover {
    background: #4688f1 url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACYAAAAmCAYAAACoPemuAAAAAXNSR0IArs4c6QAAAFBlWElmTU0AKgAAAAgAAgESAAMAAAABAAEAAIdpAAQAAAABAAAAJgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAJqADAAQAAAABAAAAJgAAAACWLw5FAAABWWlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iWE1QIENvcmUgNS40LjAiPgogICA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPgogICAgICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIgogICAgICAgICAgICB4bWxuczp0aWZmPSJodHRwOi8vbnMuYWRvYmUuY29tL3RpZmYvMS4wLyI+CiAgICAgICAgIDx0aWZmOk9yaWVudGF0aW9uPjE8L3RpZmY6T3JpZW50YXRpb24+CiAgICAgIDwvcmRmOkRlc2NyaXB0aW9uPgogICA8L3JkZjpSREY+CjwveDp4bXBtZXRhPgpMwidZAAADFUlEQVRYCe1YTUwTQRT+utvaQimtLj8ihUDQShSCAgE1YhDlZEw8eJCbiUhITIwxGvRCIB5UYuJBbhwMiR48QAx6QEL0AAeMeFCQCFYRQhpKsKUp1La73XWX2MWB2syWaHroSzYzb96b9773vdlps7qxL7xUu1ePZJK3TgE6SZZkAhXFwkQnyTamgGntSIqxFGNaGdDqn7RnbFtXPj89heCLfgizTkRcC5D4MFguG4byQzDWN2JH9RGtRKn+Cd38Efci/F2d4D+8VwPFmujLKpDZ1gE2Lz+WOe6aZmD81AR87TchrXjiBo4aDZU1sHV1R1XqUROwyPISvM1NkFb9RALWXgh9iQOSKEL4OgNRbqsi+n2lsMqgGEsm4U+jaDpj/q4OAhSTnYOMq20wHq0jcoVG3iA4OADLrc6EQCnBqBmTvKMIPD6PwCs7EGHAcFmwPewBu0f7+SGq+ItCzZi4+ATGCg/Y3T+x1l8M86Ur/wyUJsaEsUpgbXK9PlHgYDi9AB3LEvXeHQgR+mbluINFXSkdF3ReSobgvJqH4fZvAaUYhycF1SfWJMMIamD/9eYPx8dN1ELPmKlAbuWn9c3BwCzSxAhYhmylfZeOCM5HALdv45+7zUzaCedNCjUw0VINRgY2I2TitucAWuZGcKa4ngjX25pO6M/HeTwaCqtrhRx9g6g9xdwmvAwW4PJKHVyiGd0fn2Jh1a0m3TzxBST0vePVZYWrmhKSYdUYY0INzJTVgGHzRcg/0+thloNetL5ux4hrfEvYae8srj37Dpd3o42nylhY0+lbSX3BKtmXAj9wYfA6/PwaAaYwIw+OncVgdQzm/C589n6DTjTB5G6BIVAFs/w29jSnIddKzQP9zR9FMrE8gxuj9+EJ+aJLccfc0DncOdGEw0X0bVQC0pfwO315lgO9jfdQlXMwLiDFWGTJx4OzxzSDUvZqaqWy4U+Z8jjR5xyC0zcvvwiLCEXC4Ew2lHEONNhrcVJ+GLm9ici2gCWSkHZPYuXQRt+GXwqYVvJSjKUY08qAVv/kPWPKh9hkEwXTLxXeAgAZ5gWOAAAAAElFTkSuQmCC') no-repeat center left;
    border-radius: 2px;
    border: 1px solid #4688f1;
    box-shadow: 0 1px 1px rgba(0, 0, 0, .25);
    color: #fff;
    font-size: 1.4rem;
    height: 4rem;
    padding: 0 1rem 0 5rem;
    text-shadow: 0 1px 1px rgba(0, 0, 0, .25);
  }

  &-google:active,
  &-google:focus,
  &-google:hover {
    box-shadow: 0 0 10px rgba(70, 136, 241, .75);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/Btn.vue